import React, { useMemo, useEffect } from 'react';
import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import { parseISO, format } from 'date-fns';
import { format as formatTz, utcToZonedTime } from 'date-fns-tz';
import Badge from '@atlaskit/badge';
import Table from '@atlaskit/dynamic-table';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import Link from '@atlaskit/link';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { formatEstimate } from '@atlassian/jira-software-report-charting-common/src/common/utils/format-estimate/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import {
	ESTIMATION_CHANGE,
	TRANSITION_TO_DONE,
	TRANSITION_TO_NOT_DONE,
	ISSUE_REMOVED,
	ISSUE_ADDED,
	START_EVENT,
	END_EVENT,
} from '../../common/constants';
import type {
	SprintScopeChangeData,
	BurnupChartData,
	BurnupTableRow,
	SprintStartData,
	SprintEndData,
	ScopeSprintIssue,
	SprintReportsEstimationStatistic,
} from '../../common/types';
import messages from './messages';

type Props = {
	burnup: BurnupChartData;
	timeTrackingOptions: TimeTrackingOptions;
	statisticField: SprintReportsEstimationStatistic | null;
	onExperienceSuccess?: () => void;
};

const buildStartEvent = ({
	timestamp,
	issueList,
	scopeEstimate,
}: SprintStartData): BurnupTableRow => ({
	timestamp,
	type: START_EVENT,
	issues: issueList,
	prevCompletion: 0,
	completion: 0,
	prevScope: scopeEstimate,
	scope: scopeEstimate,
});

const buildEndEvent = ({
	timestamp,
	issueList,
	scope,
	completion,
}: SprintEndData): BurnupTableRow => ({
	timestamp,
	type: END_EVENT,
	issues: issueList,
	prevCompletion: completion,
	completion,
	prevScope: scope,
	scope,
});

export const buildEvents = ({
	sprintStartEvent,
	scopeChangeEvents,
	sprintEndEvent,
}: BurnupChartData): BurnupTableRow[] => {
	const startEvent: BurnupTableRow = buildStartEvent(sprintStartEvent);
	const tableScopeChangeEvents: BurnupTableRow[] = scopeChangeEvents.map(
		(event: SprintScopeChangeData): BurnupTableRow => ({
			timestamp: event.timestamp,
			type: event.eventType,
			issues: [
				{
					issueKey: event.issueKey,
					issueSummary: event.issueSummary,
					estimate: event.estimate,
				},
			],

			prevCompletion: event.prevCompletion,
			completion: event.completion,
			prevScope: event.prevScope,
			scope: event.scope,
		}),
	);

	const eventsToReturn: BurnupTableRow[] = [startEvent, ...tableScopeChangeEvents];

	if (sprintEndEvent) {
		eventsToReturn.push(buildEndEvent(sprintEndEvent));
	}

	return eventsToReturn;
};

const renderCompletedScope = (
	{ prevCompletion, completion }: BurnupTableRow,
	timeTrackingOptions: TimeTrackingOptions,
	statisticField: SprintReportsEstimationStatistic | null,
	intl: IntlShape,
) => {
	if (prevCompletion === completion) {
		return (
			<Badge max={0}>
				{formatEstimate(prevCompletion, timeTrackingOptions, statisticField, intl)}
			</Badge>
		);
	}

	return (
		<ScopeWrap>
			<Badge max={0}>
				{formatEstimate(prevCompletion, timeTrackingOptions, statisticField, intl)}
			</Badge>
			<ArrowRightIcon size="small" label={intl.formatMessage(messages.scopeArrowLabel)} />
			<Badge max={0}>{formatEstimate(completion, timeTrackingOptions, statisticField, intl)}</Badge>
		</ScopeWrap>
	);
};

const renderTotalScope = (
	{ prevScope, scope }: BurnupTableRow,
	timeTrackingOptions: TimeTrackingOptions,
	statisticField: SprintReportsEstimationStatistic | null,
	intl: IntlShape,
) => {
	if (prevScope === scope) {
		return (
			<Badge max={0}>{formatEstimate(prevScope, timeTrackingOptions, statisticField, intl)}</Badge>
		);
	}
	return (
		<ScopeWrap>
			<Badge max={0}>{formatEstimate(prevScope, timeTrackingOptions, statisticField, intl)}</Badge>
			<ArrowRightIcon size="small" label={intl.formatMessage(messages.scopeArrowLabel)} />
			<Badge max={0}>{formatEstimate(scope, timeTrackingOptions, statisticField, intl)}</Badge>
		</ScopeWrap>
	);
};

const renderTimeFromTimestamp = (timestamp: string, timeZone: string | null) => {
	const dateFnsTimestamp = parseISO(timestamp);
	if (!isNil(timeZone)) {
		return formatTz(utcToZonedTime(dateFnsTimestamp, timeZone), 'E, MMM dd yyyy, h:mmaaa');
	}
	return format(dateFnsTimestamp, 'E, MMM dd yyyy, h:mmaaa');
};

const renderIssueDetails = (issues: ScopeSprintIssue[], baseUrl: string, intl: IntlShape) => (
	<div>
		{issues.map((issue, index) => (
			<div key={index.toString()}>
				<Link href={`${baseUrl}/browse/${issue.issueKey}`}>{issue.issueKey}</Link>{' '}
				{issue.issueSummary || intl.formatMessage(messages.issueRemovedFromSprint)}
				{index < issues.length - 1 && <br />}
			</div>
		))}
	</div>
);

const renderEventType = (eventType: string, intl: IntlShape) => {
	switch (eventType) {
		case ESTIMATION_CHANGE:
			return intl.formatMessage(messages.estimateUpdated);
		case TRANSITION_TO_DONE:
			return intl.formatMessage(messages.issueCompleted);
		case TRANSITION_TO_NOT_DONE:
			return intl.formatMessage(messages.issueReopened);
		case ISSUE_REMOVED:
			return intl.formatMessage(messages.issueRemoved);
		case ISSUE_ADDED:
			return intl.formatMessage(messages.issueAdded);
		case START_EVENT:
			return intl.formatMessage(messages.sprintStarted);
		case END_EVENT:
			return intl.formatMessage(messages.sprintCompleted);
		default:
			return eventType;
	}
};

export const Grid = ({
	burnup,
	onExperienceSuccess,
	timeTrackingOptions,
	statisticField,
}: Props) => {
	const intl = useIntl();
	const events: BurnupTableRow[] = useMemo(() => buildEvents(burnup), [burnup]);
	const { baseUrl } = useTenantContext();
	const head = {
		cells: [
			{
				key: 'date',
				content: intl.formatMessage(messages.date),
				isSortable: false,
				width: 10,
			},

			{
				key: 'event',
				content: intl.formatMessage(messages.event),
				isSortable: false,
				width: 15,
			},

			{
				key: 'issue',
				content: intl.formatMessage(messages.issue),
				isSortable: false,
				width: 45,
			},

			{
				key: 'completed',
				content: intl.formatMessage(messages.completed),
				isSortable: false,
				width: 15,
			},

			{
				key: 'scope',
				content: intl.formatMessage(messages.scope),
				isSortable: false,
				width: 15,
			},
		],
	};

	const rows = events.map((event, index) => ({
		key: `row-${index}-${event.timestamp}`,
		cells: [
			{
				key: 'date',
				content: renderTimeFromTimestamp(event.timestamp, burnup.timeZone),
			},

			{
				key: 'event',
				content: renderEventType(event.type, intl),
			},

			{
				key: 'issue',
				content: renderIssueDetails(event.issues, baseUrl, intl),
			},

			{
				key: 'completed',
				content: renderCompletedScope(event, timeTrackingOptions, statisticField, intl),
			},

			{
				key: 'scope',
				content: renderTotalScope(event, timeTrackingOptions, statisticField, intl),
			},
		],
	}));

	useEffect(() => {
		if (onExperienceSuccess) {
			onExperienceSuccess();
		}
	}, [onExperienceSuccess]);
	// @ts-expect-error - TS2769 - No overload matches this call.
	return <Table head={head} rows={rows} paginationi18n={{ next: '', prev: '' }} />;
};

export default Grid;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScopeWrap = styled.div({
	display: 'inline-flex',
});
